/**
 * Agent Versioning Utilities
 * 
 * Implements a simple versioning system for agents using ID suffixes like :1, :2, :3
 * 
 * Examples:
 * - agent-123 (base agent, version 0)
 * - agent-123:1 (version 1)
 * - agent-123:2 (version 2)
 */

export interface AgentVersion {
  baseId: string;
  version: number;
  fullId: string;
}

/**
 * Parse an agent ID to extract base ID and version
 */
export function parseAgentId(agentId: string): AgentVersion {
  const parts = agentId.split(':');
  
  if (parts.length === 1) {
    // No version suffix, this is version 0 (base)
    return {
      baseId: agentId,
      version: 0,
      fullId: agentId
    };
  }
  
  if (parts.length === 2) {
    const baseId = parts[0];
    const version = parseInt(parts[1], 10);
    
    if (isNaN(version)) {
      // Invalid version, treat as base
      return {
        baseId: agentId,
        version: 0,
        fullId: agentId
      };
    }
    
    return {
      baseId,
      version,
      fullId: agentId
    };
  }
  
  // Multiple colons, treat as base
  return {
    baseId: agentId,
    version: 0,
    fullId: agentId
  };
}

/**
 * Create a new version ID for an agent
 */
export function createVersionId(baseId: string, version: number): string {
  if (version === 0) {
    return baseId;
  }
  return `${baseId}:${version}`;
}

/**
 * Get the next version number for an agent given existing versions
 */
export function getNextVersionNumber(existingVersions: AgentVersion[]): number {
  if (existingVersions.length === 0) {
    return 1;
  }
  
  const maxVersion = Math.max(...existingVersions.map(v => v.version));
  return maxVersion + 1;
}

/**
 * Group agents by their base ID and sort versions
 */
export function groupAgentsByBase(agents: any[]): Map<string, any[]> {
  const grouped = new Map<string, any[]>();
  
  agents.forEach(agent => {
    const { baseId } = parseAgentId(agent.id);
    
    if (!grouped.has(baseId)) {
      grouped.set(baseId, []);
    }
    
    grouped.get(baseId)!.push(agent);
  });
  
  // Sort versions within each group
  grouped.forEach((versions, baseId) => {
    versions.sort((a, b) => {
      const aVersion = parseAgentId(a.id).version;
      const bVersion = parseAgentId(b.id).version;
      return aVersion - bVersion;
    });
  });
  
  return grouped;
}

/**
 * Get all versions for a given base agent ID
 */
export function getAgentVersions(agents: any[], baseId: string): any[] {
  return agents
    .filter(agent => {
      const parsed = parseAgentId(agent.id);
      return parsed.baseId === baseId;
    })
    .sort((a, b) => {
      const aVersion = parseAgentId(a.id).version;
      const bVersion = parseAgentId(b.id).version;
      return aVersion - bVersion;
    });
}

/**
 * Get the latest version of an agent
 */
export function getLatestVersion(agents: any[], baseId: string): any | null {
  const versions = getAgentVersions(agents, baseId);
  return versions.length > 0 ? versions[versions.length - 1] : null;
}

/**
 * Check if an agent ID is a versioned ID
 */
export function isVersionedId(agentId: string): boolean {
  return parseAgentId(agentId).version > 0;
}

/**
 * Format version number for display
 */
export function formatVersionForDisplay(version: number): string {
  if (version === 0) {
    return "Original";
  }
  return `v${version}`;
}

/**
 * Create a duplicate agent data object for versioning
 */
export function createVersionedAgentData(originalAgent: any, newVersionId: string): any {
  const { id, createdAt, updatedAt, calls, documents, mcpServers, ...agentData } = originalAgent;
  
  return {
    ...agentData,
    id: newVersionId,
    name: `${agentData.name} (${formatVersionForDisplay(parseAgentId(newVersionId).version)})`,
    // Reset relationships for new version
    calls: { items: [] },
    documents: { items: [] },
    mcpServers: { items: [] },
  };
}

/**
 * Get the base name of an agent (removing any existing version suffix from the name)
 */
export function getBaseAgentName(agentName: string): string {
  // Remove common version patterns from the name
  return agentName
    .replace(/\s*\(v\d+\)$/, '')           // Remove (v1), (v2), etc.
    .replace(/\s*\(Version \d+\)$/, '')    // Remove (Version 1), etc.
    .replace(/\s*\(Original\)$/, '')       // Remove (Original)
    .trim();
}

/**
 * Create a proper version name for display
 */
export function createVersionName(baseName: string, version: number): string {
  const cleanBaseName = getBaseAgentName(baseName);
  if (version === 0) {
    return cleanBaseName;
  }
  return `${cleanBaseName} (${formatVersionForDisplay(version)})`;
}
